const TAX_RATE = 0.07;

export const roundPurchaseAmount = (value) =>
  Math.round((Number(value ?? 0) + Number.EPSILON) * 100) / 100;

export const getPoDisplayNumber = (poNumber) => {
  const value = String(poNumber ?? "").trim();
  if (!value) return "PO";
  const parts = value.split(/[-\s]+/).filter(Boolean);
  return `PO ${parts[parts.length - 1]}`;
};

const getProductId = (item) => item?.productId ?? item?.product?.id;

const getUsageProductId = (usage) =>
  usage?.productId ?? usage?.product?.id;

const getNormalPricePerUnit = (baseAmount, weight, item) => {
  if (weight > 0 && baseAmount !== 0) return baseAmount / weight;
  return Number(item?.pricePerUnit ?? 0);
};

const createPurchaseRow = ({
  item,
  weight,
  pricePerUnit,
  isVat = false,
  priceSource = "normal",
  poUsage = null,
}) => {
  const amountBeforeVat = roundPurchaseAmount(weight * pricePerUnit);
  const vat = isVat ? amountBeforeVat * TAX_RATE : 0;

  return {
    ...item,
    netWeight: weight,
    pricePerUnit,
    priceSource,
    poNumber: poUsage?.poNumber ?? null,
    poPricePerUnit:
      priceSource === "po" ? Number(poUsage?.purchasePrice ?? pricePerUnit) : 0,
    amountBeforeVat,
    vat,
    netPrice: amountBeforeVat + vat,
  };
};

// ถ้ามีการใช้ PO ให้แยกน้ำหนักเป็นบรรทัดราคา PO และราคาปกติ
// เพื่อไม่ให้ราคาจาก PO ถูกเฉลี่ยรวมกับราคาปกติใน Report
const getPoAdjustedPurchaseItems = (transaction, rows) => {
  const usages = (Array.isArray(transaction?.poUsageJson)
    ? transaction.poUsageJson
    : []
  )
    .map((usage) => ({
      ...usage,
      productId: getUsageProductId(usage),
      usedWeight: Number(usage?.usedWeight ?? 0),
      purchasePrice: Number(usage?.purchasePrice ?? 0),
    }))
    .filter(
      (usage) =>
        usage.productId != null &&
        usage.usedWeight > 0 &&
        Number.isFinite(usage.purchasePrice),
    );

  if (usages.length === 0) return null;

  const usageByProduct = new Map();
  usages.forEach((usage) => {
    const key = String(usage.productId);
    const list = usageByProduct.get(key) ?? [];
    list.push(usage);
    usageByProduct.set(key, list);
  });

  const usageConsumed = new Map();
  const adjustedRows = [];
  const isVat = transaction?.isVat ?? false;

  rows.forEach(({ item, weight, baseAmount }) => {
    const productId = getProductId(item);
    const matchingUsages = usageByProduct.get(String(productId)) ?? [];

    if (matchingUsages.length === 0 || weight <= 0) {
      adjustedRows.push(
        createPurchaseRow({
          item,
          weight,
          isVat,
          pricePerUnit: getNormalPricePerUnit(baseAmount, weight, item),
          priceSource: "normal",
        }),
      );
      return;
    }

    const normalPricePerUnit = getNormalPricePerUnit(
      baseAmount,
      weight,
      item,
    );
    let remainingWeight = weight;

    matchingUsages.forEach((usage) => {
      if (remainingWeight <= 0) return;

      const usageKey = usages.indexOf(usage);
      const consumed = Number(usageConsumed.get(usageKey) ?? 0);
      const available = Math.max(0, usage.usedWeight - consumed);
      const poWeight = Math.min(remainingWeight, available);
      if (poWeight <= 0) return;

      adjustedRows.push(
        createPurchaseRow({
          item,
          weight: poWeight,
          isVat,
          pricePerUnit: usage.purchasePrice,
          priceSource: "po",
          poUsage: usage,
        }),
      );
      usageConsumed.set(usageKey, consumed + poWeight);
      remainingWeight -= poWeight;
    });

    if (remainingWeight > 0) {
      adjustedRows.push(
        createPurchaseRow({
          item,
          weight: remainingWeight,
          isVat,
          pricePerUnit: normalPricePerUnit,
          priceSource: "normal",
        }),
      );
    }
  });

  return adjustedRows;
};

// กรณีไม่มี PO ใช้ยอด totalNetPrice ของตั๋วเป็นยอดเป้าหมาย
// แล้วกระจายกลับตาม item.netPrice เพื่อให้พฤติกรรมเดิมไม่เปลี่ยน
export const getAdjustedPurchaseItems = (transaction) => {
  const rows = (transaction?.items || [])
    .map((item) => ({
      item,
      weight: Number(item.netWeight ?? item.weight ?? 0),
      baseAmount: Number(item.netPrice ?? 0),
    }))
    .filter(({ weight, baseAmount }) => weight > 0 || baseAmount !== 0);

  const poAdjustedRows = getPoAdjustedPurchaseItems(transaction, rows);
  if (poAdjustedRows) return poAdjustedRows;

  const targetAmount = roundPurchaseAmount(transaction?.totalNetPrice ?? 0);
  const baseTotal = rows.reduce((sum, row) => sum + row.baseAmount, 0);
  const ratio = baseTotal !== 0 ? targetAmount / baseTotal : 0;
  const isVat = transaction?.isVat ?? false;
  let allocatedAmount = 0;

  return rows.map(({ item, weight, baseAmount }, index) => {
    const isLastItem = index === rows.length - 1;
    const amountBeforeVat = isLastItem
      ? roundPurchaseAmount(targetAmount - allocatedAmount)
      : roundPurchaseAmount(baseAmount * ratio);
    allocatedAmount += amountBeforeVat;
    const vat = isVat ? amountBeforeVat * TAX_RATE : 0;

    return {
      ...item,
      netWeight: weight,
      pricePerUnit:
        weight > 0
          ? amountBeforeVat / weight
          : Number(item.pricePerUnit ?? 0),
      priceSource: "normal",
      poNumber: null,
      poPricePerUnit: 0,
      amountBeforeVat,
      vat,
      netPrice: amountBeforeVat + vat,
    };
  });
};
